import { Flex, TabPanel, useBreakpoint, spaces } from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { Asset, isDeviceAsset } from '@/types/assets';
import AssetNotesCard from '@/pages/Assets/AssetDetailsContent/AssetNotesCard';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import {
  EntitlementSlugEnum,
  PermissionEnum,
} from '@electricjs/core_entity-client';
import { IssueCheckDisabledBanner } from '@/components/Assets/AssetDetails/IssueCheckDisabledBanner';
import DeviceManagementDetailsCard from '@/pages/Assets/AssetDetailsContent/DeviceManagementDetailsCard';
import AssetDetailsCardV2 from '@/pages/Assets/AssetDetailsContent/AssetDetailsCardV2';
import PurchaseInformationDetailsCard from '@/pages/Assets/AssetDetailsContent/PurchaseInformationDetailsCard';

type AssetDetailsPanelProps = {
  asset: Asset;
};

const AssetDetailsPanel = ({ asset }: AssetDetailsPanelProps) => {
  const { t } = useTranslation('assets');
  const { md } = useBreakpoint();
  const { space_l, space_xl } = spaces;
  const canReadInternalNotes = useUserHasOrganizationPermission(
    PermissionEnum.Warehousingread
  );
  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );

  const hasNotes = !!asset?.notes;
  const hasInternalNotes = !!asset?.internalNotes;

  const hasDeviceInfo = isDeviceAsset(asset);
  const shouldShowDeviceDetails = hasMdmEntitlement && hasDeviceInfo;

  return (
    <TabPanel>
      <IssueCheckDisabledBanner reportingData={asset.device?.reportingData} />
      <Flex
        mt={space_xl}
        gap={space_l}
        alignItems="stretch"
        flexDirection={
          md
            ? shouldShowDeviceDetails
              ? 'row'
              : 'row-reverse'
            : shouldShowDeviceDetails
              ? 'column'
              : 'column-reverse'
        }>
        <Flex vertical gap={space_l} width="100%">
          {shouldShowDeviceDetails && (
            <DeviceManagementDetailsCard
              deviceAsset={asset}
              fullWidth={hasNotes}
            />
          )}
          <PurchaseInformationDetailsCard asset={asset} />
          {hasNotes && <AssetNotesCard value={asset?.notes} />}
        </Flex>
        <Flex vertical gap={space_l} width="100%">
          <AssetDetailsCardV2 asset={asset} />
          {canReadInternalNotes && hasInternalNotes && (
            <AssetNotesCard
              cardTitle={t('assetDetailsContent.internalNotes')}
              value={asset?.internalNotes}
            />
          )}
        </Flex>
      </Flex>
    </TabPanel>
  );
};

export default AssetDetailsPanel;
